import { useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { AuthLayout, NonAuthLayout } from '@components/layout';
import { ProtectedRoute } from '@components/common';
import { analytics } from '@utils';
import { getDMSCompany } from '@/features/companies/companySlice';
import { getDashboardRoute } from '@/utils/onboardingUtils';
import RoutePermissionGate from './RoutePermissionGate';

const TALLY_SYNC_SETTINGS = '/tally-sync';

const TALLY_DATA_PREFIXES = [
  '/financial-dashboard',
  '/tally-dashboard',
  '/tally/',
  '/transactions',
  '/invoices',
  '/secondary-sales',
  '/receivable',
  '/payable',
  '/sales/distributors',
  '/sales/outlets',
  '/sales/invoices',
  '/skus',
  '/dashboard/receivables',
  '/dashboard/payables',
  '/dashboard/bank-and-cash',
  '/dashboard/party-sku-deep-dive',
];

const pathNeedsSyncEnabled = pathname =>
  TALLY_DATA_PREFIXES.some(p => pathname === p || pathname.startsWith(`${p}/`));

// const hasSyncEnabledCompany = company => {
//   if (!company) return null;
//   const subs = company.subsidiaries;
//   if (Array.isArray(subs) && subs.length > 0) {
//     return subs.some(s => s.sync_enabled || s.is_manual);
//   }
//   return !!(company.sync_enabled || company.is_manual);
// };

const isManuOrDistributor = (user, company) => {
  const roles = [
    company?.role,
    company?.role_name,
    company?.role_key,
    user?.role,
    user?.role_name,
  ]
    .filter(Boolean)
    .map(r => String(r).toLowerCase());
  return roles.includes('manufacturer') || roles.includes('distributor');
};

function SyncRouteGate({ children }) {
  const location = useLocation();
  const dispatch = useDispatch();
  const authUser = useSelector(s => s.auth.user);
  const dmsCompany = useSelector(s => s.companies.dmsCompany);
  const loading = useSelector(s => s.companies.isLoadingDMSCompany);

  const isAdmin =
    authUser?.role === 'admin' ||
    authUser?.role_name === 'Admin' ||
    authUser?.isAdmin ||
    authUser?.role_id === 1;

  const isManuDist = isManuOrDistributor(authUser, dmsCompany);

  useEffect(() => {
    if (!authUser?.id || isAdmin || !isManuDist) return;
    if (dmsCompany || loading) return;
    dispatch(getDMSCompany());
  }, [authUser?.id, isAdmin, isManuDist, dmsCompany, loading, dispatch]);

  if (isAdmin || !isManuDist) return children;

  const { pathname } = location;
  if (pathname === TALLY_SYNC_SETTINGS || pathname.startsWith(`${TALLY_SYNC_SETTINGS}/`)) {
    return children;
  }

  if (!pathNeedsSyncEnabled(pathname)) return children;

  // if (loading || (!dmsCompany && authUser?.id)) return null;

  // if (hasSyncEnabledCompany(dmsCompany) === false) {
  //   return <Navigate to={TALLY_SYNC_SETTINGS} replace />;
  // }

  return children;
}

/**
 * RouteWrapper Component
 * Wraps route components with appropriate layout and protection
 * Tracks page views for analytics
 */
const RouteWrapper = ({
  component: Component,
  layout,
  protected: isProtected = false,
}) => {
  const location = useLocation();

  if (location.pathname === '/financial-dashboard') {
    const targetRoute = getDashboardRoute();
    if (targetRoute !== '/financial-dashboard') {
      return <Navigate to={targetRoute} replace />;
    }
  }

  // Track page view on mount and route change
  useEffect(() => {
    const pageName = location.pathname;
    analytics.trackPageView(pageName, {
      layout_type: layout,
      is_protected: isProtected,
    });
  }, [location.pathname, layout, isProtected]);

  // Render component based on layout type and protection
  const renderWithLayout = () => {
    switch (layout) {
      case 'auth':
        return (
          <AuthLayout>
            <Component />
          </AuthLayout>
        );

      case 'public':
        return (
          <NonAuthLayout>
            <Component />
          </NonAuthLayout>
        );

      case 'none':
      default:
        return <Component />;
    }
  };

  // Wrap with ProtectedRoute if needed
  if (isProtected) {
    return (
      <ProtectedRoute>
        <SyncRouteGate>
          <RoutePermissionGate>{renderWithLayout()}</RoutePermissionGate>
        </SyncRouteGate>
      </ProtectedRoute>
    );
  }

  return renderWithLayout();
};

RouteWrapper.propTypes = {
  component: PropTypes.elementType.isRequired,
  layout: PropTypes.oneOf(['none', 'auth', 'public']).isRequired,
  protected: PropTypes.bool,
};

export default RouteWrapper;
